/**
 * Piezas de la card de suscripción Business que comparten la versión de la app
 * (`BusinessSubscriptionCard`) y la columna de la web de precios
 * (`BusinessPricingColumn`).
 *
 * Medidas y colores copiados de `temp_docs/CardBusiness.html`. La paleta del
 * diseño es la `stone`/`amber` de Tailwind tal cual (#1c1917 = stone-900,
 * #57534e = stone-600, #92400e = amber-800…), así que casi todo va con
 * utilidades estándar; lo que no tiene nombre va como valor arbitrario.
 *
 * Todo es PURO: ni queries ni estado de negocio. Los importes llegan ya
 * resueltos de `cartBreakdown` y se formatean con `useMoney`.
 */
import { Trans } from 'react-i18next';

import { formatPrice, localCurrencyCode } from '@/lib/priceBreakdown';
import { vatRateFor, withVat } from '@/lib/vat';

export const ACCENT = '#feae2c';

const LOCALES = { es: 'es-ES', en: 'en-GB', pt: 'pt-PT', de: 'de-DE', fr: 'fr-FR' };

export const BOLD = { b: <strong className="font-semibold" /> };

/**
 * Euros con separador de miles SIEMPRE («3.960,00 €»), como el diseño. `es-ES`
 * no agrupa por defecto las cifras de cuatro dígitos («3960,00 €») y en el
 * desglose de 30 locales eso dejaba una columna con y sin punto.
 */
function euros(cents, locale, decimals) {
  const v = (Number(cents) || 0) / 100;
  return `${v.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: 'always',
  })} €`;
}

/**
 * Formateo de importes de la card.
 *
 * - `money(c)`: siempre con dos decimales («29,90 €», «287,00 €»). Es el de las
 *   filas del desglose y los totales, donde una columna que baila se nota.
 * - `hero(c)`: el número grande; sin decimales cuando son `,00` («287 €»).
 * - `vat(c)`: el mismo importe con el IVA del país, o `null` fuera de la UE.
 */
export function useMoney(pricing, language) {
  const locale = LOCALES[language] || LOCALES.es;
  const dc = pricing?.displayCurrency;
  const rate = vatRateFor(dc?.country);
  // En divisa local manda `formatPrice` (con su «≈»); en euros, el de arriba.
  const local = !!localCurrencyCode(dc);
  const fmt = (cents, decimals) => (local ? formatPrice(cents, locale, decimals, dc) : euros(cents, locale, decimals));
  const money = (cents) => fmt(cents, 2);
  return {
    money,
    hero: (cents) => fmt(cents, Math.round(cents) % 100 === 0 ? 0 : 2),
    vat: (cents) => {
      const v = withVat(cents, rate);
      return v == null ? null : money(v);
    },
    hasVat: rate != null,
  };
}

// ── Iconos ──────────────────────────────────────────────────────────────────

export function CheckIcon({ className = '', stroke = '#b45309', strokeWidth = 2.4, size = 20 }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke}
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" className={`shrink-0 ${className}`}
    >
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

export function CloseIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#1c1917" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

export function StoreIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#57534e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0">
      <path d="M3 21h18M5 21V8l7-5 7 5v13M9 21v-6h6v6" />
    </svg>
  );
}

export function ChevronIcon({ open }) {
  return (
    <svg
      width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2.4"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      className={`transition-transform ${open ? 'rotate-180' : ''}`}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

// ── Toggle de periodo ───────────────────────────────────────────────────────

const SELECTED = 'bg-white shadow-[0_1px_3px_rgba(28,25,23,0.14)]';

/**
 * Mensual | Anual, con «ahorra un 20%» en el de anual.
 *
 * @param {object} p
 * @param {'monthly'|'annual'} p.billing
 * @param {(b: 'monthly'|'annual') => void} p.onChange
 * @param {string} p.monthlyLabel
 * @param {string} p.annualLabel
 * @param {string} p.saveLabel      «ahorra un 20%»
 * @param {string} [p.bestLabel]    la pestañita ámbar sobre «Anual» («Mejor opción»)
 * @param {string} p.heightClass    alto mínimo de los botones (cambia por variante)
 * @param {boolean} [p.stackOnMobile] en el móvil de la app el «ahorra» va DEBAJO
 *   de «Anual»; en ordenador y en la web, en la misma línea.
 * @param {boolean} [p.quietOff]    el botón no marcado en gris y seminegrita: solo
 *   en el móvil de 1 local, el resto del diseño lo deja en negro y negrita.
 */
export function PeriodToggle({
  billing, onChange, saveLabel, monthlyLabel, annualLabel, heightClass,
  stackOnMobile = false, quietOff = false, bestLabel = '',
}) {
  const annual = billing === 'annual';
  const off = quietOff
    ? 'bg-transparent font-semibold text-stone-700 lg:font-bold lg:text-stone-900'
    : 'bg-transparent font-bold text-stone-900';
  const btn = `${heightClass} rounded-[11px] border-0 font-[inherit] text-[15px] cursor-pointer transition-colors`;

  return (
    <div className="grid grid-cols-2 gap-1 rounded-[14px] bg-stone-100 p-1">
      <button
        type="button"
        aria-pressed={!annual}
        onClick={() => onChange('monthly')}
        className={`${btn} ${annual ? off : `${SELECTED} font-bold text-stone-900`}`}
      >
        {monthlyLabel}
      </button>
      <button
        type="button"
        aria-pressed={annual}
        onClick={() => onChange('annual')}
        className={`${btn} relative flex items-center justify-center ${
          stackOnMobile
            // Con la pestañita encima, en dos líneas el contenido baja un poco para
            // que «Anual» no quede debajo de ella.
            ? `flex-col gap-0.5 lg:flex-row lg:gap-2 ${bestLabel ? 'pt-2.5 lg:pt-0' : ''}`
            : 'flex-row gap-2'
        } ${annual ? `${SELECTED} font-bold text-stone-900` : off}`}
      >
        {/* La pestañita va montada sobre el borde superior del botón, fuera del
            flujo: así no cambia el alto del toggle ni empuja «Anual» hacia abajo. */}
        {bestLabel && (
          <span
            data-testid="sub-best-option"
            className="pointer-events-none absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#feae2c] px-2 py-0.5 text-[11px] font-bold leading-none text-stone-900 shadow-[0_1px_2px_rgba(28,25,23,0.18)]"
          >
            {bestLabel}
          </span>
        )}
        <span>{annualLabel}</span>
        <span className="text-xs font-semibold text-amber-800">{saveLabel}</span>
      </button>
    </div>
  );
}

// ── Stepper ─────────────────────────────────────────────────────────────────

function MinusIcon({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#1c1917" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
      <path d="M5 12h14" />
    </svg>
  );
}

function PlusIcon({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#1c1917" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/**
 * −  N  +. En los topes el botón se queda pulsable pero inerte y al 40 %: es lo
 * que hace el diseño (`aria-disabled`, no `disabled`), y así el foco no salta
 * fuera del grupo al llegar al máximo con el teclado.
 *
 * @param {object} p
 * @param {number} p.n
 * @param {number} p.max
 * @param {(n: number) => void} p.onChange
 * @param {string} p.labelledBy
 * @param {string} p.decrementLabel
 * @param {string} p.incrementLabel
 * @param {'app'|'web'} [p.size] `app` mide 44 px en móvil y 40 en ordenador; la
 *   web, 40 siempre.
 */
export function SeatsStepper({
  n, max, onChange, labelledBy, decrementLabel, incrementLabel, size = 'app',
}) {
  const atMin = n <= 1;
  const atMax = n >= max;
  const app = size === 'app';
  const box = app ? 'rounded-[14px] lg:rounded-xl' : 'rounded-xl';
  const btn = `flex items-center justify-center border-0 cursor-pointer ${
    app ? 'h-11 w-11 rounded-[10px] lg:h-10 lg:w-10 lg:rounded-[9px]' : 'h-10 w-10 rounded-[9px]'
  }`;
  const out = app
    ? 'min-w-[40px] text-[19px] lg:min-w-[36px] lg:text-lg'
    : 'min-w-[36px] text-lg';
  const icon = app ? 18 : 16;

  return (
    <div role="group" aria-labelledby={labelledBy} className={`flex shrink-0 items-center gap-1 border border-stone-200 p-1 ${box}`}>
      <button
        type="button"
        data-testid="sub-seats-minus"
        aria-label={decrementLabel}
        aria-disabled={atMin}
        onClick={() => { if (!atMin) onChange(n - 1); }}
        className={`${btn} bg-stone-100 ${atMin ? 'opacity-40' : ''}`}
      >
        <MinusIcon size={icon} />
      </button>
      <output data-testid="sub-seats-value" aria-live="polite" className={`text-center font-bold tabular-nums ${out}`}>
        {n}
      </output>
      <button
        type="button"
        data-testid="sub-seats-plus"
        aria-label={incrementLabel}
        aria-disabled={atMax}
        onClick={() => { if (!atMax) onChange(n + 1); }}
        className={`${btn} bg-[#fff3dc] ${atMax ? 'opacity-40' : ''}`}
      >
        <PlusIcon size={icon} />
      </button>
    </div>
  );
}

// ── Desglose ────────────────────────────────────────────────────────────────

export function BreakdownRow({ label, value, discount = false }) {
  return (
    <div className="flex justify-between gap-3 text-sm text-stone-700">
      <span>{label}</span>
      <span className={`whitespace-nowrap ${discount ? 'text-green-700' : 'text-stone-900'}`}>{value}</span>
    </div>
  );
}

/**
 * La línea de Total dentro de la caja del desglose (ordenador y web). En el
 * móvil de la app el total va en el pie fijo, con otro tamaño.
 */
export function BoxTotal({ label, total, suffix, vatLine }) {
  return (
    <div className="flex flex-col gap-0.5 border-t border-stone-200 pt-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[15px] font-bold">{label}</span>
        <span className="whitespace-nowrap">
          <strong data-testid="sub-total" className="text-[19px] font-extrabold">{total}</strong>{' '}
          <span className="text-[13px] text-stone-600">{suffix}</span>
        </span>
      </div>
      {vatLine && <div className="text-right text-[13px] text-stone-600">{vatLine}</div>}
    </div>
  );
}

/** Una viñeta con `<b>` traducida, para las listas que lo llevan. */
export function RichText({ i18nKey }) {
  return <Trans i18nKey={i18nKey} components={BOLD} />;
}
